import type { SceneBeat } from "@/lib/timeline/book/BookLayout";
import { formatChapterHeading } from "@/lib/timeline/book/ChapterId";
import { SCENE_MARK_HALF_LEN, sceneChangeMarks } from "@/lib/timeline-gpu/scene-marks";
import type { RailBranch } from "@/data/rail-branches";
import { timelineRailAxisY } from "@/lib/timeline/marker/MarkerMetrics/metrics";

const LABEL_GAP_PX = 8;

export interface ChapterTick {
  chapterId: string;
  label: string;
  startMs: number;
  locationLabel: string;
}

export interface ChapterTickPose {
  chapterId: string;
  label: string;
  startMs: number;
  x: number;
  y: number;
}

/** One label at the first beat of every chapter, including chapter 1. */
export function chapterTicks(beats: readonly SceneBeat[]): ChapterTick[] {
  const ticks: ChapterTick[] = [];
  let previousId: string | undefined;
  for (const beat of beats) {
    const chapterId = beat.chapterId;
    if (chapterId === undefined || chapterId === previousId) {
      continue;
    }
    ticks.push({
      chapterId,
      label: formatChapterHeading(chapterId, beat.chapterTitle ?? ""),
      startMs: beat.startMs,
      locationLabel: beat.locationLabel,
    });
    previousId = chapterId;
  }
  return ticks;
}

export function chapterTickPoses(
  ticks: readonly ChapterTick[],
  branches: readonly RailBranch[],
  viewportWidth: number,
): ChapterTickPose[] {
  const axisY = timelineRailAxisY();
  return ticks.flatMap((tick) => {
    const marks = sceneChangeMarks(
      [{ startMs: tick.startMs, locationLabel: tick.locationLabel }],
      branches,
      axisY,
      viewportWidth,
    );
    return marks.map((mark) => ({
      chapterId: tick.chapterId,
      label: tick.label,
      startMs: tick.startMs,
      x: mark.x,
      y: mark.y - SCENE_MARK_HALF_LEN - LABEL_GAP_PX,
    }));
  });
}
